import { Injectable, Optional } from "@angular/core";
import { LocalStorageConfig } from "./trycatch-local-store.config";

function getWindow(): any {
	return window;
}

@Injectable({
	providedIn: "root"
})
export class LocalStorageService {
	private _config: LocalStorageConfig = <LocalStorageConfig>{};

	private _storage: any;
	private _window: Window;
	private _localStorageSupported = false;

	public get localStorageSupported() {
		return this._localStorageSupported;
	}

	constructor(@Optional() config: LocalStorageConfig) {
		if (config) {
			this._config = config;
		}

		this._window = getWindow();
		this._localStorageSupported = typeof Storage !== "undefined";

		if (this._localStorageSupported) {
			if (this._config.storageType === "session") {
				this._storage = this._window.sessionStorage;
			} else {
				this._storage = this._window.localStorage;
			}
		}
	}

	private storage(storageType?: "session" | "local"): Storage {
		return storageType
			? storageType === "session"
				? this._window.sessionStorage
				: this._window.localStorage
			: this._storage;
	}

	public setInSessionStorage(key: string, object: any): boolean {
		return this.set(key, object, "session");
	}

	public setInLocalStorage(key: string, object: any): boolean {
		return this.set(key, object, "local");
	}

	public set(
		key: string,
		object: any,
		storageType?: "session" | "local"
	): boolean {
		if (!this._localStorageSupported) {
			return false;
		}

		// getting storage from this.storage method which override the default storage type passed with config
		const storage = this.storage(storageType);

		const objectType = typeof object;
		storage.setItem(key + "-type", objectType);

		if (objectType === "object") {
			object = JSON.stringify(object);
		}

		storage.setItem(key, object);
		return true;
	}

	public getFromSessionStorage(key: string): any {
		return this.get(key, "session");
	}

	public getFromLocalStorage(key: string): any {
		return this.get(key, "local");
	}

	public get(key: string, storageType: "session" | "local" = "session"): any {
		if (!this._localStorageSupported) {
			return undefined;
		}

		// getting storage from this.storage method which override the default storage type passed with config
		const storage = this.storage(storageType);

		const objectType = storage.getItem(key + "-type");
		let retObj = storage.getItem(key);
		if (!retObj) {
			return undefined;
		}

		if (objectType === "object") {
			retObj = JSON.parse(retObj);
		}
		return retObj;
	}

	public deleteFromSessionStorage(key: string): boolean {
		return this.delete(key, "session");
	}

	public deleteFromLocalStorage(key: string): boolean {
		return this.delete(key, "local");
	}

	public delete(
		key: string,
		storageType: "session" | "local" = "session"
	): boolean {
		if (!this._localStorageSupported) {
			return false;
		}

		// getting storage from this.storage method which override the default storage type passed with config
		const storage = this.storage(storageType);

		storage.removeItem(key);
		storage.removeItem(`${key}-type`);
		return true;
	}

	public clearSessionStorage(): boolean {
		return this.clear("session");
	}

	public clearLocalStorage(): boolean {
		return this.clear("local");
	}

	public clear(storageType: "session" | "local" = "session"): boolean {
		if (!this._localStorageSupported) {
			return false;
		}
		const dealer = this.getFromLocalStorage("dealer");
		const kiosk = this.getFromLocalStorage("kiosk");

		const isFlowTransition = this.getFromSessionStorage("sales-flow-transition");
		const gnafId = this.getFromSessionStorage("gnaf_id");
		const aboutYou = this.getFromSessionStorage("cart-about-you");
		const energyAvailability = this.getFromSessionStorage("energy-availability");
		const addressFromEnergy = this.getFromSessionStorage("address-energy");
		const addressFromNBN = this.getFromSessionStorage("address-nbn");

		// getting storage from this.storage method which override the default storage type passed with config
		const storage = this.storage(storageType);

		storage.clear();
		if (!dealer && !kiosk && isFlowTransition) {
			this.setInSessionStorage("sales-flow-transition", isFlowTransition);
			this.setInSessionStorage("cart-about-you", aboutYou);
			this.setInSessionStorage("gnaf_id", gnafId);
			this.setInSessionStorage("energy-availability", energyAvailability);
			this.setInSessionStorage("address-energy", addressFromEnergy);
			this.setInSessionStorage("address-nbn", addressFromNBN);
		}

		return true;
	}
}
